import type { FC, PointerEvent, ReactNode } from 'react'
import type { PhotoId } from '@/components/generals/photo'
import type { CompanyName } from '@/components/art/wordmark'

import { useRef } from 'react'
import { Heart, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { delay, vars } from '@/lib/motion'
import { Wordmark } from '@/components/art/wordmark'
import { Photo } from '@/components/generals/photo'

export const SCOUT_MOCK_WIDTH = 840
export const SCOUT_MOCK_NARROW_WIDTH = 420

interface ScoutFrameProps {
    children: ReactNode
    className?: string
}

/** Drawn at a fixed 840 × 577 (420 × 577 on phones, with `@max-[600px]:` layouts) and scaled to its column: translucent frame chrome around an inner card. Its contents play once when it enters. */
const ScoutFrame: FC<ScoutFrameProps> = ({ children, className }): ReactNode => {
    return (
        <div aria-hidden='true' className={cn('reveal-group relative h-[577px] w-full rounded-[22px] border-[0.5px] border-ink/15 bg-white/40 p-3', className)}>
            <div className='relative size-full overflow-hidden rounded-[10px] border-[0.5px] border-ink/15 bg-[#f8f6f5] shadow-[0_1px_2px_rgb(16_24_40/0.05)]'>
                {children}
            </div>
        </div>
    )
}

interface CollectiveHeaderProps {
    /** When true the avatar is already in place (no entrance). */
    settled?: boolean
    chipsDelay?: number
}

const CollectiveHeader: FC<CollectiveHeaderProps> = ({ settled = false, chipsDelay = 1700 }): ReactNode => {
    return (
        <>
            <div className='collective-avatar' data-settled={settled ? '' : undefined}>
                <Photo id='elena' sizes='200px' portrait className='aspect-square w-full rounded-full ring-[3px] ring-paper' />
            </div>

            <div className='absolute left-[12%] top-[7.5%] flex items-center gap-3'>
                <p className={cn('font-serif text-[20px] text-ink', !settled && 'rise')} style={settled ? undefined : delay(1600)}>
                    Maren's Collective
                </p>
                <span className={cn('rounded-full bg-paper px-2.5 py-1 text-[13px] text-ink/70 shadow-[0_0_0_1px_rgb(40_38_34/0.12)] @max-[600px]:hidden', !settled && 'pop')} style={settled ? undefined : delay(chipsDelay)}>
                    AI Creative Direction
                </span>
                <span className={cn('rounded-full bg-paper px-2.5 py-1 text-[13px] text-ink/70 shadow-[0_0_0_1px_rgb(40_38_34/0.12)] @max-[600px]:hidden', !settled && 'pop')} style={settled ? undefined : delay(chipsDelay + 120)}>
                    Brand Design
                </span>
            </div>

            <div className='absolute inset-x-[5%] top-[19%] h-px bg-ink/10' />
        </>
    )
}

/* Build ------------------------------------------------------------------------------ */

interface Tile {
    photo: PhotoId
    comment?: string
}

const TILES: Tile[] = [
    { photo: 'nora', comment: 'That poster series has been living in my head all week' },
    { photo: 'luca' },
    { photo: 'sofia', comment: 'Ships fast and the craft never dips. Rare combo' },
    { photo: 'mateo' },
    { photo: 'elena', comment: 'Her type sensibility sold three of my clients already' },
    { photo: 'adrian' }
]

const BURST = [[0, -14], [12, -8], [12, 7], [0, 14], [-12, 7], [-12, -8]]

export const BuildWindow: FC = (): ReactNode => {
    const rowRef = useRef<HTMLDivElement>(null)

    const handleMove = (event: PointerEvent<HTMLDivElement>) => {
        const row = rowRef.current

        if (!row) return

        row.querySelectorAll<HTMLElement>('[data-tile]').forEach(tile => {
            const rect = tile.getBoundingClientRect()
            const distance = (event.clientX - (rect.left + rect.width / 2)) / rect.width
            const lift = 9 * Math.exp(-distance * distance * 1.4)

            tile.style.setProperty('--lift', `${lift.toFixed(2)}px`)
        })
    }

    const handleLeave = () => {
        rowRef.current?.querySelectorAll<HTMLElement>('[data-tile]').forEach(tile => tile.style.setProperty('--lift', '0px'))
    }

    return (
        <ScoutFrame>
            <CollectiveHeader />

            <div
                ref={rowRef}
                onPointerMove={handleMove}
                onPointerLeave={handleLeave}
                className='pointer-events-auto absolute inset-x-[5%] top-[36%] grid grid-cols-7 gap-[1.6%] @max-[600px]:top-[27%] @max-[600px]:grid-cols-4 @max-[600px]:gap-x-[4%] @max-[600px]:gap-y-9'
            >
                {TILES.map((tile, index) => (
                    <div key={tile.photo} data-tile='' className='wave-tile group/tile relative'>
                        <div className='pop relative aspect-[4/5] overflow-hidden rounded-[8px] bg-pane shadow-[0_0_0_1px_rgb(40_38_34/0.1)]' style={delay(1900 + index * 160)}>
                            <Photo id={tile.photo} sizes='110px' portrait className='absolute inset-0 size-full' />
                            {/* a torn paper strip across the eyes, for privacy */}
                            <div className='torn absolute inset-x-[-6%] top-[25%] h-[15%] bg-paper [rotate:-4deg]' style={vars({ '--tear': '3px' })} />
                        </div>

                        <span className='pop absolute -right-[10%] -top-[8%] flex size-[34%] items-center justify-center rounded-full bg-paper shadow-[0_2px_6px_-2px_rgb(40_38_34/0.35)]' style={delay(2150 + index * 160)}>
                            <Heart className='size-[55%] fill-clay text-clay' strokeWidth={1.5} />
                            {BURST.map(([x, y]) => (
                                <span key={`${x}-${y}`} className='burst-dot' style={vars({ '--bx': `${x}px`, '--by': `${y}px`, '--d': `${2200 + index * 160}ms` })} />
                            ))}
                        </span>

                        {tile.comment && (
                            <span className='pop absolute -bottom-[16%] left-1/2 -translate-x-1/2' style={delay(2500 + index * 160)}>
                                <span className='flex size-[26px] items-center justify-center rounded-full bg-ink text-paper'>
                                    <MessageCircle className='size-[60%]' strokeWidth={2} />
                                </span>
                                <span className='pointer-events-none absolute bottom-[130%] left-1/2 z-20 w-[190px] -translate-x-1/2 rounded-[10px] bg-ink px-3 py-2.5 text-center text-[12.5px] leading-[1.35] text-paper opacity-0 transition-opacity duration-300 group-hover/tile:opacity-100'>
                                    {tile.comment}
                                </span>
                            </span>
                        )}
                    </div>
                ))}

                <div data-tile='' className='wave-tile relative'>
                    <div className='pop flex aspect-[4/5] items-center justify-center rounded-[8px] border border-dashed border-ink/30' style={delay(1900 + TILES.length * 160)}>
                        <span className='hand text-[24px] [rotate:-6deg] text-ink/70 leading-[1.05]'>
                            you?
                        </span>
                    </div>
                </div>
            </div>

            <p className='rise absolute bottom-[8%] left-[5%] text-[13px] text-ink/55' style={delay(3200)}>
                6 designers invited · 3 comments
            </p>
        </ScoutFrame>
    )
}

/* Intro ------------------------------------------------------------------------------ */

interface Arrival {
    name: CompanyName
    className: string
}

const ARRIVALS: Arrival[] = [
    { name: 'Halyard', className: 'left-[9%] top-[30%] @max-[600px]:left-[6%] @max-[600px]:top-[23%]' },
    { name: 'Pendulum', className: 'right-[9%] top-[29%] @max-[600px]:right-[6%] @max-[600px]:top-[23%]' },
    { name: 'Tessel', className: 'right-[6%] top-[62%]' },
    { name: 'Kiln', className: 'left-[7%] top-[66%]' },
    { name: 'Northfold', className: 'left-[40%] top-[84%]' }
]

export const IntroWindow: FC = (): ReactNode => {
    return (
        <ScoutFrame>
            <CollectiveHeader chipsDelay={1500} />

            <div className='absolute inset-x-[18%] top-[38%] flex flex-col items-center text-center @max-[600px]:inset-x-[8%] @max-[600px]:top-[35%]'>
                <p className='rise font-serif text-[36px] leading-[1.15] tracking-[-0.03em] text-ink @max-[600px]:text-[30px]' style={delay(1900)}>
                    Where companies apply to interview you
                </p>
                <span className='pop mt-[5%] rounded-full bg-ink px-5 py-2 text-[14px] text-paper shadow-[0_0_0_1px_rgb(26_24_21/0.9),inset_0_1px_0_rgb(255_255_255/0.2)]' style={delay(2300)}>
                    Enter
                </span>
            </div>

            {ARRIVALS.map((arrival, index) => (
                <span
                    key={arrival.name}
                    className={cn('rise absolute rounded-full bg-paper px-4 py-2 shadow-[0_0_0_1px_rgb(40_38_34/0.1),0_6px_14px_-8px_rgb(40_38_34/0.4)]', arrival.className)}
                    style={delay(2600 + index * 180)}
                >
                    <Wordmark name={arrival.name} className='text-[15px] text-ink/85' />
                </span>
            ))}
        </ScoutFrame>
    )
}

/* Earn ------------------------------------------------------------------------------- */

const TOTAL = '82,500'

const EARNINGS = [
    ['Ada to Halyard', '$30,000'],
    ['Filip to Northfold', '$27,500'],
    ['Noor to Tessel', '$25,000']
]

export const EarnWindow: FC = (): ReactNode => {
    let digitIndex = 0

    return (
        <ScoutFrame>
            <CollectiveHeader settled />

            <div className='absolute inset-x-[8%] top-[27%] flex flex-col items-center'>
                <p className='text-[13px] text-ink/55'>
                    Earned this year
                </p>
                <p className='mt-[2%] flex items-start font-serif text-[76px] leading-none tracking-[-0.03em] text-ink @max-[600px]:text-[64px]'>
                    <span>$</span>
                    {TOTAL.split('').map((character, index) => {
                        if (!/\d/.test(character)) return <span key={index}>{character}</span>

                        const order = digitIndex

                        digitIndex += 1

                        return (
                            <span key={index} className='odo-digit'>
                                <span className='odo-strip' style={vars({ '--n': Number(character), '--d': `${300 + order * 140}ms` })}>
                                    {Array.from({ length: 10 }, (_, digit) => (
                                        <span key={digit} className='block h-[1em]'>
                                            {digit}
                                        </span>
                                    ))}
                                </span>
                            </span>
                        )
                    })}
                </p>
            </div>

            <div className='absolute inset-x-[16%] top-[58%] @max-[600px]:inset-x-[8%] @max-[600px]:top-[52%]'>
                <p className='rise text-[13px] font-medium uppercase tracking-[0.08em] text-ink/60' style={delay(1200)}>
                    Earnings breakdown
                </p>
                <dl className='mt-[3%] flex flex-col gap-3 text-[16px]'>
                    {EARNINGS.map(([label, amount], index) => (
                        <div key={label} className='row-in flex items-baseline gap-2' style={delay(1500 + index * 220)}>
                            <dt className='text-ink/80'>{label}</dt>
                            <span aria-hidden='true' className='flex-1 translate-y-[-3px] border-b border-dotted border-ink/35' />
                            <dd className='font-medium text-ink'>{amount}</dd>
                        </div>
                    ))}
                </dl>
            </div>
        </ScoutFrame>
    )
}
